import React, { useState, useEffect } from 'react';
import ProjectCreationForm from './ProjectCreationForm';
import ProjectList from './ProjectList';
import { ApiService } from '../../services/api';
import { Project } from '../../types';
import { useProject } from '../../contexts/ProjectContext';

const Projects: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use the project context to update the global project list
  const { setProjectList } = useProject();
  
  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getProjects();
      setProjects(response.data);
      // Update the global project list in the context
      setProjectList(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
      setLoading(false);
    }
  };

  // Load projects on component mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const handleProjectCreated = (newProject: Project) => {
    const updatedProjects = [...projects, newProject];
    setProjects(updatedProjects);
    // Update the global project list in the context
    setProjectList(updatedProjects);
    setShowModal(false);
  };
  
  const handleProjectDelete = async (projectId: number) => {
    try {
      setLoading(true);
      await ApiService.deleteProject(projectId);
      // Remove the deleted project from state
      const updatedProjects = projects.filter((project: Project) => project.id !== projectId);
      setProjects(updatedProjects);
      // Update the global project list in the context
      setProjectList(updatedProjects);
      setLoading(false);
    } catch (err) {
      console.error(`Error deleting project ${projectId}:`, err);
      setError('Failed to delete project. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="p-3">
      <h1 className="text-2xl font-bold mb-1 text-gray-800">Projects</h1>
      <p className="text-gray-600 mb-4 text-sm">Create and manage data analytics projects</p>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 text-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex flex-col space-y-4">
        {/* Create Project Section */}
        <div className="bg-blue-50 rounded-xl p-4">
          <h2 className="text-lg font-medium text-blue-700 mb-1">The Project Creator</h2>
          <p className="text-gray-600 mb-3 text-sm">
            Our project management system allows you to create and organize your data analytics workflows.
            Create a new project to start analyzing your data with our comprehensive toolset.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors duration-200 text-sm"
          >
            New Project
          </button>
        </div>

        {/* Project Selection Section */}
        <div>
          <h2 className="text-lg font-medium text-gray-800 mb-2">Available Projects</h2>
          {loading && !error ? (
            <div className="flex justify-center items-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <ProjectList 
              projects={projects} 
              onDeleteProject={handleProjectDelete}
            />
          )}
        </div>
      </div>

      {/* Modal Overlay */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowModal(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                <h3 className="text-lg font-medium text-blue-700">Create New Project</h3>
                <p className="text-blue-600 text-sm mt-1">Fill in the details to create your project</p>
              </div>
              <div className="p-4">
                <ProjectCreationForm onProjectCreated={handleProjectCreated} />
              </div>
              <div className="bg-gray-50 px-4 py-2.5 flex justify-end">
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 font-medium mr-4"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
